import dayjs from 'dayjs'
import { download, http } from './request'

/** 与 yj-admin-ui 安全日志（dev/log/oplog）及后端 DevLogPageParam.logLevel 一致 */
export type SecurityLogLevel = 'error' | 'warn' | 'info'

const DEV_LOG_BASE = '/dev/log'

/** 模块筛选关键词（匹配类名或请求路径），与 yj-admin-ui oplog 下拉一致 */
export const SECURITY_LOG_MODULE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'Auth', label: '认证授权' },
  { value: 'Case', label: '案件' },
  { value: 'Personnel', label: '人员' },
  { value: 'User', label: '用户' },
  { value: 'Config', label: '配置' },
]

export interface SecurityLogStats {
  totalCount: number
  errorCount: number
  warnCount: number
  infoCount: number
  /** Tab 数量，来自 /dev/log/op/securityStats */
  tabAll: number
  tabError: number
  tabWarning: number
  tabInfo: number
  errorDeltaFromYesterday: number
  warningDeltaFromYesterday: number
  infoDeltaFromYesterday: number
  availableRate: number
  statusText: string
  updatedAt: string
}

export interface SecurityLogRecord {
  id: string
  level: SecurityLogLevel
  title: string
  module: string
  username: string
  ip: string
  reason: string
  occurredAt: string
  /** 列表无法从日志接口得知是否已锁定；封禁成功后刷新列表仍为 false，与 yj-admin-ui 行为一致 */
  blocked: boolean
  /** 详情页展示 */
  className?: string
  reqUrl?: string
  methodName?: string
  /** 日志完整性：PASSED / TAMPERED / UNSIGNED / DISABLED */
  integrityStatus?: string
  /** 日志完整性中文：完整 / 已篡改 / 未签名 / 未启用 */
  integrityStatusName?: string
}

/** 详情-日志完整性状态 */
export function formatLogIntegrity(status: unknown, statusName?: unknown): string {
  const name = String(statusName ?? '').trim()
  if (name) return name
  const u = String(status ?? '').toUpperCase()
  if (u === 'PASSED') return '完整'
  if (u === 'TAMPERED') return '已篡改'
  if (u === 'UNSIGNED') return '未签名'
  if (u === 'DISABLED') return '未启用'
  return String(status ?? '').trim()
}

export interface SecurityLogPageParams {
  page: number
  pageSize: number
  level?: SecurityLogLevel
  keyword?: string
  /** 日志名称筛选（来自 dev_log.name 下拉） */
  name?: string
  dateFrom?: string
  dateTo?: string
}

export interface SecurityLogPageResult {
  list: SecurityLogRecord[]
  total: number
}

interface DevLogRaw {
  id?: string
  category?: string
  name?: string
  exeStatus?: string
  exeMessage?: string
  opIp?: string
  opUser?: string
  opTime?: string | number
  className?: string
  reqUrl?: string
  methodName?: string
  reqMethod?: string
  integrityStatus?: string
  integrityStatusName?: string
}

interface SpringPageDevLog {
  records?: DevLogRaw[]
  list?: DevLogRaw[]
  total?: number
}

interface DevLogSecurityStatsRaw {
  errorTotal?: number
  errorDeltaFromYesterday?: number
  warningTotal?: number
  warningDeltaFromYesterday?: number
  infoTotal?: number
  infoDeltaFromYesterday?: number
  tabAll?: number
  tabError?: number
  tabWarning?: number
  tabInfo?: number
  systemStatus?: string
  availabilityPercent?: number | string
  serverTime?: number
}

function levelToLogLevelParam(level?: SecurityLogLevel): string {
  if (!level) return 'ALL'
  if (level === 'error') return 'ERROR'
  if (level === 'warn') return 'WARNING'
  return 'INFO'
}

function formatOpTime(t: unknown): string {
  if (t == null || t === '') return '—'
  if (typeof t === 'number' && Number.isFinite(t)) {
    const d = dayjs(t)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : '—'
  }
  if (typeof t === 'string') {
    const d = dayjs(t)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : t
  }
  return '—'
}

function mapLogLevelFromDevLog(row: DevLogRaw): SecurityLogLevel {
  if (row.category === 'EXCEPTION') return 'error'
  if (row.category === 'OPERATE' && row.exeStatus === 'FAIL') return 'warn'
  return 'info'
}

function moduleLabel(row: DevLogRaw): string {
  const cn = row.className || row.reqUrl || ''
  if (!cn) return '系统模块'
  const short = cn.split('.').pop() || cn
  return short.length > 24 ? `${short.slice(0, 24)}…` : short
}

function reasonText(msg: string | undefined, truncate: boolean): string {
  const m = msg?.trim()
  if (!m) return '—'
  if (!truncate || m.length <= 80) return m
  return `${m.slice(0, 80)}…`
}

export function mapDevLogToRecord(
  row: DevLogRaw,
  opts?: { fullReason?: boolean },
): SecurityLogRecord {
  const fullReason = opts?.fullReason === true
  return {
    id: row.id ?? '',
    level: mapLogLevelFromDevLog(row),
    title: row.name?.trim() || '—',
    module: moduleLabel(row),
    username: row.opUser?.trim() || '—',
    ip: row.opIp?.trim() || '—',
    reason: reasonText(row.exeMessage, !fullReason),
    occurredAt: formatOpTime(row.opTime),
    blocked: false,
    className: row.className,
    reqUrl: row.reqUrl,
    methodName: row.methodName ?? row.reqMethod,
    integrityStatus: row.integrityStatus,
    integrityStatusName: row.integrityStatusName,
  }
}

function toDevLogQuery(params: SecurityLogPageParams): Record<string, string | number> {
  const out: Record<string, string | number> = {
    current: params.page,
    size: params.pageSize,
    logLevel: levelToLogLevelParam(params.level),
  }
  const sk = params.keyword?.trim()
  if (sk) out.searchKey = sk
  const nk = params.name?.trim()
  if (nk) out.nameKeyword = nk
  const df = params.dateFrom?.trim()
  const dt = params.dateTo?.trim()
  if (df) out.startOpTime = df
  if (dt) out.endOpTime = dt
  return out
}

function exportQueryString(params: SecurityLogPageParams): string {
  const q = new URLSearchParams()
  const flat = toDevLogQuery(params)
  Object.entries(flat).forEach(([k, v]) => {
    q.set(k, String(v))
  })
  return q.toString()
}

export async function fetchSecurityLogStats(): Promise<SecurityLogStats> {
  const raw = await http.get<DevLogSecurityStatsRaw>(`${DEV_LOG_BASE}/op/securityStats`)
  const avail = raw.availabilityPercent
  let availableRate = 0
  if (typeof avail === 'number' && Number.isFinite(avail)) availableRate = avail
  else if (typeof avail === 'string') {
    const n = parseFloat(avail)
    if (!Number.isNaN(n)) availableRate = n
  }
  const tabAll = Number(raw.tabAll ?? 0)
  return {
    totalCount: tabAll,
    errorCount: Number(raw.errorTotal ?? 0),
    warnCount: Number(raw.warningTotal ?? 0),
    infoCount: Number(raw.infoTotal ?? 0),
    tabAll,
    tabError: Number(raw.tabError ?? 0),
    tabWarning: Number(raw.tabWarning ?? 0),
    tabInfo: Number(raw.tabInfo ?? 0),
    errorDeltaFromYesterday: Number(raw.errorDeltaFromYesterday ?? 0),
    warningDeltaFromYesterday: Number(raw.warningDeltaFromYesterday ?? 0),
    infoDeltaFromYesterday: Number(raw.infoDeltaFromYesterday ?? 0),
    availableRate,
    statusText: raw.systemStatus === 'STABLE' ? '稳定' : '需关注',
    updatedAt: raw.serverTime != null ? dayjs(raw.serverTime).format('YYYY-MM-DD HH:mm:ss') : '—',
  }
}

export async function fetchSecurityLogPage(
  params: SecurityLogPageParams,
): Promise<SecurityLogPageResult> {
  const raw = await http.get<SpringPageDevLog>(`${DEV_LOG_BASE}/page`, {
    params: toDevLogQuery(params),
  })
  const rows = Array.isArray(raw.records) ? raw.records : Array.isArray(raw.list) ? raw.list : []
  const total = typeof raw.total === 'number' ? raw.total : 0
  return {
    list: rows.map((r) => mapDevLogToRecord(r)),
    total,
  }
}

export async function fetchSecurityLogDetail(id: string): Promise<SecurityLogRecord | null> {
  if (!id) return null
  const raw = await http.get<DevLogRaw>(`${DEV_LOG_BASE}/detail`, { params: { id } })
  if (!raw || typeof raw !== 'object') return null
  return mapDevLogToRecord(raw, { fullReason: true })
}

export async function fetchSecurityLogNameOptions(): Promise<string[]> {
  const raw = await http.get<string[]>('/dev/log/op/nameOptions')
  const arr = Array.isArray(raw) ? raw : []
  return arr.map((s) => String(s ?? '').trim()).filter((s) => s.length > 0)
}

export async function exportSecurityLogs(params: SecurityLogPageParams): Promise<void> {
  const qs = exportQueryString(params)
  await download(qs ? `${DEV_LOG_BASE}/export?${qs}` : `${DEV_LOG_BASE}/export`)
}

/** 与 yj-admin-ui 一致：清空操作日志与异常日志（不按当前筛选条件） */
export async function clearSecurityLogs(): Promise<void> {
  await http.post<void>(`${DEV_LOG_BASE}/delete`, { category: 'OPERATE' })
  await http.post<void>(`${DEV_LOG_BASE}/delete`, { category: 'EXCEPTION' })
}

/** 按主键删除当前列表中的一条安全日志（后端仅允许 OPERATE / EXCEPTION） */
export async function deleteSecurityLogRow(id: string): Promise<void> {
  const trimmed = (id ?? '').trim()
  if (!trimmed) throw new Error('日志ID为空')
  await http.post<void>(`${DEV_LOG_BASE}/deleteRow`, { id: trimmed })
}

/** @deprecated 使用 SECURITY_LOG_MODULE_OPTIONS */
export function getSecurityLogModuleOptions(): string[] {
  return SECURITY_LOG_MODULE_OPTIONS.map((x) => x.label)
}
